import React, { useState, useEffect } from "react";
import DropdownNew, { DropdownOption } from "./Dropdown";
import CalendarIcon from "../../../assets/images/DateGray.svg";
import { CgClose } from "react-icons/cg";
import Input from "./Input";
import Button from "react-bootstrap/Button";
import { useAuth } from "../../../firebase/AuthProvider";
import { getInterimTemplates } from "../../../services/taskServices/TaskService";
import InterimTemplateResponse from "../../../interfaces/task/InterimTemplateResponse";
import { publishTemplateInterimTask, publishTask } from "../../../services/taskServices/TaskService";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import NewTask from "../../../interfaces/task/NewTask";
import { getTaskLKValues } from "../../../services/taskServices/TaskLKService";
import TaskCreationLKValues from "../../../interfaces/lkValues/TaskCreationLKValues";
import { GetAllParticipantUnderMeAsync } from "../../../services/participantServices/ParticipantService";
import { useNotifications } from "../../../contexts/NotificationProvider";

interface LogActivityModalProps {
    onClose: () => void;
}

const Close = CgClose as React.ComponentType;

export default function NewTaskComponent({ onClose }: LogActivityModalProps) {
    const { currentUser, currentOrganization, currentRoles } = useAuth();
    const navigate = useNavigate();

    const [templates, setTemplates] = useState<InterimTemplateResponse[]>([]);
    const [templateOptions, setTemplateOptions] = useState<DropdownOption[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | number>("");

    // Task creation state
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [assignee, setAssignee] = useState<string | number>("");
    const [participant, setParticipant] = useState<string | number>("");
    const [dueDate, setDueDate] = useState("");
    const [coAssignee, setCoAssignee] = useState<string | number>("");

    // Lookup values
    const [lkOptions, setLkOptions] = useState<TaskCreationLKValues | null>(null);
    const [taskOrganizationOptions, setTaskOrganizationOptions] = useState<DropdownOption[]>([]);
    const [taskTypeOptions, setTaskTypeOptions] = useState<DropdownOption[]>([]);
    const [taskSubTypeOptions, setTaskSubTypeOptions] = useState<DropdownOption[]>([]);
    const [assigneeOptions, setAssigneeOptions] = useState<DropdownOption[]>([]);
    const [participantOptions, setParticipantOptions] = useState<DropdownOption[]>([]);

    // Loading states
    const [isSC, setIsSC] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loading, setLoading] = useState<{ participant: boolean; assignee: boolean }>({
        participant: false,
        assignee: false
    });

    // Validation errors
    const [errors, setErrors] = useState<Record<string, string>>({});
    const { notify } = useNotifications();
    // Fetch templates and lookup values on component mount
    useEffect(() => {
        const fetchData = async () => {
            if (currentUser) {
                try {
                    // Fetch templates
                    const authToken = await currentUser.getIdToken();
                    const templateResponse = await getInterimTemplates(authToken);
                    if (templateResponse) {
                        setTemplates(templateResponse);
                        const formattedTemplates = templateResponse.map(template => ({
                            label: template.title,
                            value: template.id
                        }));
                        setTemplateOptions(formattedTemplates);
                    }

                    // Fetch lookup values
                    const lkResponse = await getTaskLKValues(authToken);
                    if (lkResponse) {
                        setLkOptions(lkResponse);
                    }
                } catch (error) {
                    console.error("Error fetching data:", error);
                    toast.error("Failed to load data");
                }
            }
        };

        fetchData();
    }, [currentUser]);

    // Process lookup values when they change
    useEffect(() => {
        if (lkOptions && currentUser) {
            // Check if user is SC (highest role)
            const isSCHighestRole = currentRoles?.length === 1 && currentRoles[0].name === "SC";
            setIsSC(isSCHighestRole);

            if (isSCHighestRole && currentUser) {
                setAssignee(currentUser.uid);
            }

            // Format lookup options
            const formattedTaskOrganizations = lkOptions.taskOrganizations.map(org => ({
                value: org.id.toString(),
                label: org.type,
            }));

            const formattedTaskTypes = lkOptions.taskTypes.map(tt => ({
                value: tt.id.toString(),
                label: tt.type,
            }));

            const formattedTaskSubTypes = lkOptions.taskSubTypes.map(tst => ({
                value: tst.id.toString(),
                label: tst.type,
            }));

            setTaskOrganizationOptions(formattedTaskOrganizations);
            setTaskTypeOptions(formattedTaskTypes);
            setTaskSubTypeOptions(formattedTaskSubTypes);

            // let formattedAssignees;
            // if (isSCHighestRole) {
            //     formattedAssignees = [{
            //         value: currentUser?.uid,
            //         label: currentUser?.displayName ? currentUser.displayName : 'Assign to yourself',
            //     }];
            // } else {
            //     formattedAssignees = lkOptions.users.map(user => ({
            //         value: user.id.toString(),
            //         label: user.displayName,
            //     }));
            // }

            if (!isSCHighestRole) {
                // Add 'Assigned to you' option as the first option
                const assignedToYouOption = [{
                    value: currentUser?.uid,
                    label: currentUser?.displayName ? `Assign to ${currentUser.displayName}` : 'Assign to yourself',
                }];

                const assignelkoption = lkOptions.users.map(user => ({
                        value: user.id.toString(),
                        label: user.displayName,
                    })).filter(u => u.value != currentUser.uid);

                const formattedAssignees = [
                    ...assignedToYouOption,
                    ...assignelkoption
                ];
                setAssigneeOptions(formattedAssignees);
            } else {
                setAssigneeOptions([]);
            }

            // setAssigneeOptions(formattedAssignees);
        }
    }, [lkOptions, currentUser, currentRoles]);

    // Fetch participants when assignee changes
    useEffect(() => {
        getAllParticipantUnderMe();
    }, [assignee]);

    // Update task fields when template is selected
    useEffect(() => {
        if (selectedTemplateId && templates.length > 0) {
            const template = templates.find(t => t.id === selectedTemplateId);
            if (template) {
                setTitle(template.title);
                setDescription(template.description);
                // Set default due date based on template
                if (template.daysFromCreationDueDate) {
                    const dueDate = new Date();
                    dueDate.setDate(dueDate.getDate() + template.daysFromCreationDueDate);
                    setDueDate(dueDate.toISOString().split('T')[0]);
                }
            }
        } else {
            // Reset fields when no template is selected
            setTitle("");
            setDescription("");
            setDueDate("");
        }
    }, [selectedTemplateId, templates]);

    const getAllParticipantUnderMe = async () => {
        if (!currentUser || !assignee) {
            setParticipantOptions([]);
            return;
        }

        try {
            setLoading(prev => ({ ...prev, participant: true }));
            setParticipantOptions([]); // clear list
            setParticipant(""); // clear participant

            const token = await currentUser.getIdToken();
            const res = await GetAllParticipantUnderMeAsync(token, assignee.toString());

            if (res.success && res.data) {
                const formattedParticipants = res.data.map(part => ({
                    value: part.id.toString(),
                    label: part.firstName + " " + part.lastName,
                }));
                setParticipantOptions(formattedParticipants);
            }
        } catch (error) {
            console.error("Error fetching participants:", error);
        } finally {
            setLoading(prev => ({ ...prev, participant: false }));
        }
    };

    const handleTemplateChange = (value: string | number) => {
        setSelectedTemplateId(value);
        // Clear error when user interacts with the field
        if (errors.template) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.template;
                return newErrors;
            });
        }
    };

    const validateField = (name: string, value: any) => {
        let errorMessage = "";

        switch (name) {
            case "title":
                if (!value || value.trim() === "") {
                    errorMessage = "Title is required.";
                }
                break;
            case "description":
                if (!value || value.trim() === "") {
                    errorMessage = "Description is required.";
                }
                break;
            case "dueDate":
                if (!value) {
                    errorMessage = "Due date is required.";
                }
                break;
            case "assignee":
                if (!value && !isSC) {
                    errorMessage = "Assignee is required.";
                }
                break;
            case "participant":
                if (!value) {
                    errorMessage = "Participant is required.";
                }
                break;
            default:
                break;
        }

        return errorMessage;
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        // // Validate template selection
        // if (!selectedTemplateId) {
        //     newErrors.template = "Template selection is required.";
        // }

        // Validate all fields
        const validations = [
            validateField("title", title),
            validateField("description", description),
            validateField("dueDate", dueDate),
            validateField("assignee", assignee),
            validateField("participant", participant),
        ];

        const fieldNames = ["title", "description", "dueDate", "assignee", "participant"];

        fieldNames.forEach((field, index) => {
            if (validations[index]) {
                newErrors[field] = validations[index];
            }
        });

        setErrors(newErrors);

        // Return true if no errors
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate form before submission
        if (!validateForm()) {
            notify("Please fix validation errors before submitting", { type: 'error' });
            return;
        }

        if (!currentUser || !currentOrganization) {
            toast.error("Authentication error. Please log in again.");
            return;
        }

        setIsSubmitting(true);
        try {
            const authToken = await currentUser.getIdToken();

            // Prepare task data with correct typing
            const taskData: NewTask = {
                title,
                description,
                requiredActions: "",
                requiredFiles: selectedTemplateId
                    ? templates.find(t => t.id === selectedTemplateId)?.requiredFiles.map(f => f.id.toString()) || []
                    : [],
                assignee: assignee.toString(),
                // coAssignee: coAssignee ? coAssignee.toString() : undefined,
                participant: participant.toString(),
                templateTaskId: selectedTemplateId ? selectedTemplateId.toString() : undefined,
                taskOrganization: "1",
                taskType: "1",
                taskSubType: "4",
                dueDate,
                priority: "Low" as "Low" | "Medium" | "High",
                workflowGroup: "Standard"
            };

            let taskId: number | null = null;

            // Conditionally call the appropriate API based on whether a template is selected
            if (selectedTemplateId) {
                // Submit task using template
                taskId = await publishTemplateInterimTask(authToken, taskData);
            } else {
                // Submit task without template
                taskId = await publishTask(authToken, taskData);
            }

            if (taskId) {
                toast.success("Task created successfully!");
                // Navigate to the new task
                navigate(`/task/${currentOrganization.abbreviation}-${taskId}`);
                onClose(); // Close the modal
            } else {
                toast.error("Failed to create task");
            }
        } catch (error) {
            console.error("Error creating task:", error);
            toast.error("Failed to create task. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle input changes with validation
    const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value;

        // Update the appropriate state field
        switch (field) {
            case "title":
                setTitle(value);
                break;
            case "description":
                setDescription(value);
                break;
            case "dueDate":
                setDueDate(value);
                break;
        }

        // Clear error when user interacts with the field
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleAssigneeChange = (value: string | number) => {
        setAssignee(value);
        setParticipant(""); // Clear participant when assignee changes

        if (errors.assignee) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.assignee;
                return newErrors;
            });
        }
    };

    const handleParticipantChange = (value: string | number) => {
        setParticipant(value);

        if (errors.participant) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.participant;
                return newErrors;
            });
        }
    };

    const handleCoAssigneeChange = (value: string | number) => {
        setCoAssignee(value);
    };

    // Check if a template is selected
    const isTemplateSelected = selectedTemplateId !== "";

    const clearTemplate = () => {
        setSelectedTemplateId("");
        setTitle("");
        setDescription("");
        setDueDate("");
    };

    return (
        <div className="p-6 md:p-8">
            {/* Title */}
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Create New Task</h2>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                    aria-label="Close"
                >
                    <Close />
                </button>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="space-y-5">
                    <DropdownNew
                        label="Select from template"
                        name="template"
                        value={selectedTemplateId}
                        onChange={handleTemplateChange}
                        options={templateOptions}
                        placeholder="Select a template"
                        error={errors.template}
                    />

                    {isTemplateSelected && (
                        <button
                            type="button"
                            onClick={clearTemplate}
                            className="text-sm text-[#7e57c2] hover:underline self-end"
                        >
                            Remove template
                        </button>
                    )}

                    <Input
                        label="Task Title"
                        name="title"
                        value={title}
                        placeholder="Give your task a title..."
                        onChange={handleInputChange("title")}
                        disabled={isTemplateSelected}
                        error={errors.title}
                    />

                    {isTemplateSelected && (
                        <p className="mt-1 text-xs text-gray-500" style={{ color: "red" }}>
                            Auto-filled from template
                        </p>
                    )}


                    <div className="flex items-start gap-x-4">
                        {/* <div className="w-1/2">
                            <DropdownNew
                                label="Assignee"
                                name="assignee"
                                value={assignee}
                                onChange={handleAssigneeChange}
                                options={assigneeOptions}
                                placeholder={loading.assignee ? "Loading..." : "Select assignee..."}
                                error={errors.assignee}
                            // disabled={isTemplateSelected}
                            />
                        </div> */}

                        {/* <div className="w-1/2">
                            {isSC ? (
                                <Input
                                    label="Assignee"
                                    name="assignee"
                                    value={currentUser?.displayName || "Assigned to you"}
                                    disabled
                                    onChange={() => { }}
                                />
                            ) : (
                                <DropdownNew
                                    label="Assignee"
                                    name="assignee"
                                    value={assignee}
                                    onChange={handleAssigneeChange}
                                    options={assigneeOptions}
                                    placeholder={loading.assignee ? "Loading..." : "Select assignee..."}
                                    error={errors.assignee}
                                />
                            )}
                        </div> */}

                        <div className="w-1/2">
                            {isSC ? (
                                <>
                                    <Input
                                        label="Assignee"
                                        name="assignee"
                                        value={currentUser?.displayName || "Assigned to you"}
                                        disabled
                                        onChange={() => { }}
                                    />
                                    <p className="mt-1 text-xs text-gray-500" style={{ color: "red" }}>
                                        Auto-filled from template
                                    </p>
                                </>
                            ) : (
                                <DropdownNew
                                    label="Assignee"
                                    name="assignee"
                                    value={assignee}
                                    onChange={handleAssigneeChange}
                                    options={assigneeOptions}
                                    placeholder={loading.assignee ? "Loading..." : "Select assignee..."}
                                    error={errors.assignee}
                                />
                            )}
                        </div>

                        <div className="w-1/2">
                            <Input
                                type="date"
                                label="Due Date"
                                name="dueDate"
                                value={dueDate}
                                onChange={handleInputChange("dueDate")}
                                icon={CalendarIcon}
                                error={errors.dueDate}
                                disabled={isTemplateSelected}
                                min={new Date().toISOString().split("T")[0]}
                            />

                            {isTemplateSelected && (
                                <p className="mt-1 text-xs text-gray-500" style={{ color: "red" }}>
                                    Auto-filled from template
                                </p>
                            )}

                        </div>
                    </div>

                    <div className="w-full">
                        <DropdownNew
                            label="Participant"
                            name="participant"
                            value={participant}
                            onChange={handleParticipantChange}
                            options={participantOptions}
                            placeholder={loading.participant ? "Loading..." : "Select participant..."}
                            error={errors.participant}
                        // disabled={isTemplateSelected}
                        />
                    </div>

                    {/* {assignee && !isSC && (
                        <div className="w-full">
                            <DropdownNew
                                label="Co-Assignee (Optional)"
                                name="coAssignee"
                                value={coAssignee}
                                onChange={handleCoAssigneeChange}
                                options={assigneeOptions.filter(user => user.value !== assignee)}
                                placeholder="Select co-assignee..."
                            />
                        </div>
                    )} */}

                    <Input
                        label="Task Description"
                        type="textarea"
                        name="description"
                        value={description}
                        placeholder="A brief description of what the task entails..."
                        onChange={handleInputChange("description")}
                        error={errors.description}
                        disabled={isTemplateSelected}
                    />

                    {isTemplateSelected && (
                        <p className="mt-1 text-xs text-gray-500" style={{ color: "red" }}>
                            Auto-filled from template
                        </p>
                    )}


                    <div className="modal_footer border-t pt-3 flex justify-end gap-2">
                        <Button variant="secondary" onClick={onClose} type="button">
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Creating..." : "Create Task"}
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    );
}